import {useCallback} from 'react';

import {useConfig} from '@/contexts/config';
import {VisibilitySet} from '@/engine';
import {serializeDocument} from '@/utils/document/serializer';
import {getLocaleFromState} from '@/utils/locale';
import {useDocumentRepository} from '../../repository';
import {DocumentStore} from '../../store';
import {useDocumentUtils} from '../../utils';

type Props = {
  visibilitySet: VisibilitySet;
  isResendingDocument?: boolean;
};

export const useOnDocumentLoaded = ({visibilitySet, isResendingDocument}: Props) => {
  const {model} = useConfig();
  const {store, valueResolver} = useDocumentUtils();
  const {saveDocument} = useDocumentRepository();

  const setLocaleFromDocument = useCallback(
    (state: DocumentStore) => {
      const locale = getLocaleFromState(state, model);
      if (locale) {
        state.changeLocale(locale);
      }
    },
    [model],
  );

  const updateDocumentMeta = useCallback(
    async (state: DocumentStore) => {
      if (state.meta && !isResendingDocument) {
        // Update the document so the latest transaction is the last one seen.
        await saveDocument(
          serializeDocument({data: state.data, meta: state.meta}, model, visibilitySet, valueResolver),
        );
      }
    },
    [isResendingDocument, model, saveDocument, valueResolver, visibilitySet],
  );

  return useCallback(async () => {
    const state = store.getState();

    setLocaleFromDocument(state);
    await updateDocumentMeta(state);
  }, [store, setLocaleFromDocument, updateDocumentMeta]);
};
